import { Environment, LeagueConfig } from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
/**
 * @ts-ignore is used here due to @leagueplatform/legacy-locales being a
 * temporary measure that is served as a JS utility.
 */
// @ts-ignore
import { legacyStrings } from '@leagueplatform/legacy-locales';
import {
  AuthConfig,
  AuthlessLeagueConfig,
  DynamicSdkConfig,
  JourneyHeaderBackground,
  LinkHandler,
  LoadingSpinner,
  MASONRY,
  useLeagueConfigFirebaseUpdater,
  BENEFITS_DETAIL_ROOT_NODE_ID,
  BENEFITS_LANDING_ROOT_NODE_ID,
} from '@highmark-web/highmark-common';
import { getWalletConfig } from 'utils/get-wallet-config.util';
import {
  CLIENT_ID,
  APP_STORE_PROD_URL,
  PLAY_STORE_PROD_URL,
  TENANT_ID,
} from '../common/constants';
import { EN_STRING_OVERRIDES } from '../locales/sdk-string-overrides/en';
import { bcbswyTheme } from '../theme/theme';
import { GroupLogo } from '../components/group-logo.component';
import { ASSETS_CONFIG } from './asset-config';
import { getClaimsConfig } from '../utils/get-claims-config.util';
import { getLiveChatConfig } from '../utils/get-live-chat-config.util';
import { firebaseRemoteConfig } from '../common/firebase/firebase';

// SDK Initializers that use League's Config API
const AUTHLESS_LEAGUE_CONFIG: AuthlessLeagueConfig = {
  core: {
    clientId: CLIENT_ID,
    tenantId: TENANT_ID,
    // We do not set `auth` yet - it will be added dynamically once we know whether we're in standalone or embedded (mobile auth) mode
    api: {
      wsUrl: import.meta.env.VITE_API_URL as string,
      url: import.meta.env.VITE_REST_API_URL as string,
      legacyRestApi: import.meta.env.VITE_LEGACY_REST_API as string,
    },
    contentUrl: import.meta.env.VITE_CONTENT_SERVER_URL as string,
    appEnvironment: import.meta.env.VITE_ENV as Environment,
    routing: {
      adapter: createReactRouterAdapter(),
      handleExternalLink: LinkHandler.handleLink,
    },
    mobileAppUrls: {
      appStoreUrl: APP_STORE_PROD_URL,
      playStoreUrl: PLAY_STORE_PROD_URL,
    },
    i18n: {
      strings: {
        default: { ...legacyStrings.default, ...EN_STRING_OVERRIDES },
        en: { ...legacyStrings.en, ...EN_STRING_OVERRIDES },
      },
    },
    ui: {
      theme: bcbswyTheme,
      components: {
        loading: { element: <LoadingSpinner />, text: false },
        pageHeader: {
          shape: 'line',
          backgroundColor: 'surface.background.secondary',
        },
      },
      groupLogo: { element: <GroupLogo /> },
    },
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
    shouldUseLegacyActivityToolboxLayout: true,
  },
  assets: {
    overrides: ASSETS_CONFIG,
  },
  healthProfile: {
    ratingsBarEnabled: false,
    careSdkEnabled: true,
    chatWithProfessionalEnabled: false,
  },
  healthJourney: {
    tabs: ['activities', 'progress', 'history', 'explore'],
    headerConfig: {
      backgroundImageSrc: JourneyHeaderBackground,
    },
  },
  careDiscovery: {
    masonryAppId: {
      left: MASONRY.APP_ID_CARE_DISCOVERY_HOME,
    },
  },
  aboutMe: {
    showAccountBanner: false,
    showLandingPageBackButton: true,
  },
  benefits: {
    benefitsLandingMasonryConfigKey: BENEFITS_LANDING_ROOT_NODE_ID,
    benefitsDetailsMasonryConfigKey: BENEFITS_DETAIL_ROOT_NODE_ID,
  } as LeagueConfig['benefits'],
  __care__adjustForCardColorThemeDiscrepancy: true,
} as AuthlessLeagueConfig;

const getDynamicSdkConfig = (): DynamicSdkConfig => ({
  ...{},
  claims: getClaimsConfig(),
  wallet: getWalletConfig(),
  liveChat: getLiveChatConfig(),
});

export const useGetLeagueConfig = (
  authConfig: AuthConfig | undefined,
): LeagueConfig => {
  const sdkConfigWithRemoteFlagValues = useLeagueConfigFirebaseUpdater(
    firebaseRemoteConfig,
    getDynamicSdkConfig,
  );

  return {
    ...AUTHLESS_LEAGUE_CONFIG,
    core: {
      ...AUTHLESS_LEAGUE_CONFIG.core,
      auth: authConfig,
    } as LeagueConfig['core'],
    ...sdkConfigWithRemoteFlagValues,
  };
};
